import * as React from "react";
import {repository, session} from "clientInstance";
import {DataBaseComponent, DataBaseComponentState, Refresh} from "components/DataBaseComponent/DataBaseComponent";
import {RouteComponentProps} from "react-router";
import PaperLayout from "components/PaperLayout/PaperLayout";
import * as moment from "moment";
import {
    TaskResource,
    EnvironmentResource,
    OctopusServerNodeResource,
    ProjectResource,
    TenantResource,
    TaskState,
} from "client/resources";
import {PagingDataTable} from "components/PagingDataTable/PagingDataTable";
import InternalLink from "components/Navigation/InternalLink/InternalLink";
import Select, {Item} from "components/form/Select/Select";
import DateFormatter from "utils/DateFormatter";
import {TaskName} from "client/resources/taskResource";
import TaskDetails from "components/TaskDetails/TaskDetails";
import styles = require("./style.less");
import AdvancedFilterLayout, {AdvancedFilterCheckbox} from "components/AdvancedFilterLayout";
import NavigationButton, {NavigationButtonType} from "components/Button/NavigationButton";
import PermissionCheck, {hasPermission} from "components/PermissionCheck/PermissionCheck";
import Permission from "client/resources/permission";
import TaskLayout from "areas/tasks/TaskLayout/TaskLayout";
import {StatsResourceCollection} from "client/repositories/taskRepository";
import {isAllowed} from "components/PermissionCheck/PermissionCheck";
import routeLinks from "../../../../routeLinks";
import { FeatureToggle, Feature } from "components/FeatureToggle";
import { QueryStringFilters, IQuery } from "components/QueryStringFilters/QueryStringFilters";
import { arrayValueFromQueryString } from "utils/ParseHelper/ParseHelper";

export interface Filter {
    ids?: string[];
    state?: TaskFilterState;
    project?: string;
    environment?: string;
    name?: TaskName;
    node?: string;
    tenant?: string;
    hasPendingInterruptions?: boolean;
    hasWarningsOrErrors?: boolean;
}

export interface TasksQuery extends IQuery {
    serverNode?: string;
    state?: string;
    ids?: string[];
    project?: string;
    environment?: string;
    name?: string;
    tenant?: string;
    hasPendingInterruptions?: string;
    hasWarningsOrErrors?: string;
}

interface TasksState extends DataBaseComponentState {
    tasks?: StatsResourceCollection;
    projects: ProjectResource[];
    environments: EnvironmentResource[];
    nodes: OctopusServerNodeResource[];
    tenants: TenantResource[];
    currentPageIndex: number; // We manage our own paging due to automatic refresh / timers.
    filter: Filter;
    hasLoadedOnce?: boolean;
}

class TaskResourceDataTable extends PagingDataTable<TaskResource<any>> {
}

class FilterLayout extends AdvancedFilterLayout<Filter> {
}

export enum TaskFilterState {
    Incomplete = "Incomplete",
    Running = "Running",
    Completed = "Completed",
    Unsuccessful = "Unsuccessful",
    Queued = "Queued",
    Executing = "Executing",
    Cancelling = "Cancelling",
    Success = "Success",
    Canceled = "Canceled",
    TimedOut = "TimedOut",
    Failed = "Failed"
}

function getTaskStatesFromFilterState(taskFilterState: TaskFilterState) {
    switch (taskFilterState) {
        case TaskFilterState.Incomplete:
            return [TaskState.Queued, TaskState.Executing, TaskState.Cancelling].join(",");
        case TaskFilterState.Running:
            return [TaskState.Executing, TaskState.Cancelling].join(",");
        case TaskFilterState.Completed:
            return [TaskState.Canceled, TaskState.Success, TaskState.Failed, TaskState.TimedOut].join(",") ;
        case TaskFilterState.Unsuccessful:
            return [TaskState.Canceled, TaskState.Failed, TaskState.TimedOut].join(",");
        case TaskFilterState.Queued:
            return TaskState.Queued;
        case TaskFilterState.Executing:
            return TaskState.Executing;
        case TaskFilterState.Cancelling:
            return TaskState.Cancelling;
        case TaskFilterState.Success:
            return TaskState.Success;
        case TaskFilterState.Canceled:
            return TaskState.Canceled;
        case TaskFilterState.TimedOut:
            return TaskState.TimedOut;
        case TaskFilterState.Failed:
            return TaskState.Failed;
    }
}

function getFilter(query: TasksQuery): Filter {
    return {
        node: query.serverNode,
        state: TaskFilterState[query.state as keyof typeof TaskFilterState],
        ids: arrayValueFromQueryString(query.ids),
        hasPendingInterruptions: query.hasPendingInterruptions === "true",
        hasWarningsOrErrors: query.hasWarningsOrErrors === "true",
        environment: query.environment,
        project: query.project,
        tenant: query.tenant,
        name: TaskName[query.name as keyof typeof TaskName]
    };
}

export function getQuery(filter: Filter): TasksQuery {
    return {
        serverNode: filter.node,
        state: filter.state,
        ids : filter.ids,
        environment: filter.environment,
        hasPendingInterruptions: filter.hasPendingInterruptions ? "true" : undefined,
        hasWarningsOrErrors: filter.hasWarningsOrErrors ? "true" : undefined,
        name: filter.name,
        project: filter.project,
        tenant: filter.tenant
    };
}

class TasksQueryStringFilters extends QueryStringFilters<Filter, TasksQuery> {}

export class Tasks extends DataBaseComponent<RouteComponentProps<any>, TasksState> {
    private taskNames: Item[] = [
        {value: TaskName.Backup, text: "Backup"}, // Obsoleted, but kept so you can see tasks from 2.6
        {value: TaskName.Deploy, text: "Deployment"},
        {value: TaskName.Health, text: "Health check"},
        {value: TaskName.AdHocScript, text: "Script console script"},
        {value: TaskName.AutoDeploy, text: "Auto-deploy"},
        {value: TaskName.ConfigureLetsEncrypt, text: "Let's encrypt"},
        {value: TaskName.Upgrade, text: "Tentacle upgrade"},
        {value: TaskName.TestEmail, text: "Send test email"},
        {value: TaskName.TestAzureAccount, text: "Test azure account"},
        {value: TaskName.TestAccount, text: "Test Account"},
        {value: TaskName.Retention, text: "Apply retention policies"},
        {value: TaskName.Delete, text: "Delete document"},
        {value: TaskName.SystemIntegrityCheck, text: "System integrity check"},
        {value: TaskName.SyncCommunityActionTemplates, text: "Sync community action templates"},
        {value: TaskName.SynchronizeBuiltInPackageRepositoryIndex, text: "Sync built-in package repository"},
        {value: TaskName.UpdateCalamari, text: "Update calamari"},
        {value: TaskName.Subscription, text: "Process subscriptions"},
        {value: TaskName.MigrationSpacePartialExport, text: "Migration - partial export"},
        {value: TaskName.MigrationSpaceImport, text: "Migration - import"},
        {value: TaskName.SynchronizeExternalSecurityGroupsForUsers, text: "Synchronize external security groups"},
    ].sort((a, b) => a.text.localeCompare(b.text));

    constructor(props: any) {
        super(props);
        this.state = {
            currentPageIndex: 0,
            filter: createEmptyFilter(),
            environments: [],
            nodes: [],
            projects: [],
            tenants: []
        };
    }

    componentDidMount() {
        return this.doBusyTask(async () => {
            const getProjects = hasPermission(Permission.ProjectView) ? repository.Projects.all() : Promise.resolve([]);
            const getEnvironments = hasPermission(Permission.EnvironmentView) ? repository.Environments.all() : Promise.resolve([]);
            const getNodes = repository.OctopusServerNodes.all();
            const getTenants = repository.Tenants.all();

            this.setState({
                projects: await getProjects,
                environments: await getEnvironments,
                nodes: await getNodes,
                tenants: await getTenants
            });
            this.doRefresh = await this.createRefreshLoop(() => this.searchInternal(this.state.filter, this.state.currentPageIndex), 5000);
        });
    }
    search(filter: Filter) {
        this.setState({filter, hasLoadedOnce: false, currentPageIndex: 0}, async () => await this.doRefresh());
    }

    async searchInternal<K extends keyof Filter>(filter: Filter, currentPageIndex: number) {
        this.setState({currentPageIndex});

        const tasks = await repository.Tasks.list({
            states: getTaskStatesFromFilterState(filter.state),
            project: filter.project,
            environment: filter.environment,
            name: filter.name,
            node: filter.node,
            tenant: filter.tenant,
            skip: this.state.tasks ? currentPageIndex * this.state.tasks.ItemsPerPage : 0,
            ids: filter.ids && filter.ids.length ? filter.ids.join(",") : undefined,
            hasPendingInterruptions: filter.hasPendingInterruptions ? true : null,
            hasWarningsOrErrors: filter.hasWarningsOrErrors ? true : null,
        });

        return {
            tasks,
            hasLoadedOnce: true,
        };
    }

    clear() {
        return this.search(createEmptyFilter());
    }

    render() {
        const list = this.state.tasks &&
            <TaskResourceDataTable
                initialData={this.state.tasks}
                onRow={(item: any) => this.buildRow(item)}
                onRowRedirectUrl={(task: TaskResource<any>) => `tasks/${task.Id}`}
                rowColumnClassName={styles.taskDetailsCell}
                headerColumns={["", "State", "Start Time", "Completed Time", "Duration"]}
                onEmpty={this.handleOnEmpty}
                showPagingInNumberedStyle={true}
                currentPageIndex={this.state.currentPageIndex}
                onPageSelected={async (skip: number, p: number) => {
                    this.setState({hasLoadedOnce: false, currentPageIndex: p}, async () => await this.doRefresh());
                }}/>;

        const filterSections = [
            {
                render: <div>
                    <AdvancedFilterCheckbox
                        label="Awaiting manual intervention"
                        value={this.state.filter.hasPendingInterruptions}
                        onChange={hasPendingInterruptions => this.search({ ...this.state.filter, hasPendingInterruptions })}
                    />
                    <AdvancedFilterCheckbox
                        label="Has warnings or errors"
                        value={this.state.filter.hasWarningsOrErrors}
                        onChange={hasWarningsOrErrors => this.search({ ...this.state.filter, hasWarningsOrErrors })}
                    />
                    <Select value={this.state.filter.name}
                            onChange={(name: TaskName) => this.search({...this.state.filter, name})}
                            items={this.taskNames}
                            allowClear={true}
                            fieldName="task type"
                            hintText="All task types"
                            label="By task type"/>
                    <PermissionCheck permission={Permission.ProjectView} wildcard={true}>
                        <Select value={this.state.filter.project}
                                onChange={project => this.search({...this.state.filter, project})}
                                items={this.state.projects.map(p => ({value: p.Id, text: p.Name}))}
                                allowClear={true}
                                allowFilter={true}
                                fieldName="project"
                                hintText="All projects"
                        />
                    </PermissionCheck>
                    <PermissionCheck permission={Permission.EnvironmentView} wildcard={true}>
                        <Select value={this.state.filter.environment}
                                onChange={environment => this.search({...this.state.filter, environment})}
                                items={this.state.environments.map(e => ({value: e.Id, text: e.Name}))}
                                allowClear={true}
                                allowFilter={true}
                                fieldName="environment"
                                hintText="All environments"
                        />
                    </PermissionCheck>
                    <Select value={this.state.filter.node}
                            onChange={node => this.search({...this.state.filter, node})}
                            items={this.state.nodes.map(n => ({value: n.Id, text: n.Name}))}
                            allowClear={true}
                            label="By node"
                            hintText="All nodes"/>
                    <FeatureToggle feature={Feature.MultiTenancy}>
                        <PermissionCheck permission={Permission.TenantView} tenant="*">
                            <Select value={this.state.filter.tenant}
                                onChange={tenant => this.search({...this.state.filter, tenant})}
                                items={this.state.tenants.map(t => ({value: t.Id, text: t.Name}))}
                                allowClear={true}
                                allowFilter={true}
                                fieldName="tenant"
                                hintText="All tenants"
                                label="By tenant"/>
                        </PermissionCheck>
                    </FeatureToggle>
                </div>
            }
        ];

        const stateFilter = <div className={styles.states}>
                <Select value={this.state.filter.state}
                        onChange={(state: TaskFilterState) => this.search({...this.state.filter, state})}
                        items={Object.keys(TaskFilterState).map(t => ({value: t, text: t}))}
                        allowClear={true}
                        fieldName="task state"
                        hintText="All task states"
                />
        </div>;

        return <TaskLayout>
            <TasksQueryStringFilters
                filter={this.state.filter}
                onFilterChange={filter => this.search(filter)}
                getFilter={getFilter}
                getQuery={getQuery}
            />
            <PaperLayout title="Tasks"
                         busy={this.state.busy}
                         enableLessIntrusiveLoadingIndicator={this.state.hasLoadedOnce}
                         errors={this.state.errors}
                         sectionControl={<PermissionCheck permission={[Permission.AdministerSystem, Permission.TaskCreate]}  wildcard={true}>
                             <NavigationButton label="Script Console" href={routeLinks.tasks.console} type={NavigationButtonType.Primary}/>
                         </PermissionCheck>}
                         fullWidth={true}>
                {this.state.tasks && this.state.tasks.TotalCounts &&
                <StatisticsPanel totals={this.state.tasks.TotalCounts}
                                 setFilter={filter => this.search(filter)}/>}
                <FilterLayout
                    filter={this.state.filter}
                    defaultFilter={createEmptyFilter()}
                    additionalHeaderFilters={[stateFilter]}
                    onFilterReset={resetFilter => this.search(resetFilter)}
                    filterSections={filterSections}
                    renderContent={() => list}
                />
            </PaperLayout>
        </TaskLayout>;
    }

    private doRefresh: Refresh = () => Promise.resolve();

    private handleOnEmpty = () => {
        return (
            <div>No tasks found</div>
        );
    }

    private buildRow = (task: TaskResource<any>) => {
        return [
            // mark.siedle: We want this InternalLink here so users have the option of standard
            // anchor-behaviour (ie. right click) that you don't get with the onClick from our SimpleDataTable component.
            <InternalLink to={routeLinks.task(task).root}><TaskDetails task={task}
                                                                       stripTopBottomPadding={true}/></InternalLink>,
            task.State,
            task.State === TaskState.Queued
                ? DateFormatter.dateToLongFormat(task.QueueTime) + " (" + moment(task.QueueTime).fromNow() + ")"
                : DateFormatter.dateToLongFormat(task.StartTime),
            DateFormatter.dateToLongFormat(task.CompletedTime),
            task.State === TaskState.Queued ? null : task.Duration,
        ];
    }
}

interface StatisticsPanelProps {
    totals: { [state: string]: number };
    setFilter(filter: Filter): void;
}

const StatisticsPanel: React.StatelessComponent<StatisticsPanelProps> = props => {
    const addSIfNeeded = (length: number) => {
        return length > 1 ? "s" : "";
    };

    const stat = () => {
        const result: JSX.Element[] = [];
        const setFilter = (e: any, filter: Filter) => {
            e.preventDefault();
            props.setFilter(filter);
        };

        if (props.totals) {
            if (props.totals.Interrupted > 0) {
                result.push(<span key="interrupted">{props.totals.Interrupted} task{addSIfNeeded(props.totals.Interrupted)} <a
                    href="#" onClick={e => setFilter(e, {hasPendingInterruptions: true})}>awaiting intervention</a></span>);
            }
            if (props.totals.Executing > 0) {
                result.push(<span key="executing">{props.totals.Executing} task{addSIfNeeded(props.totals.Executing)} <a
                    href="#" onClick={e => setFilter(e, {state: TaskFilterState.Executing})}>running</a></span>);
            }
            if (props.totals.Cancelling > 0) {
                result.push(<span key="cancelling">{props.totals.Cancelling} task{addSIfNeeded(props.totals.Cancelling)} <a
                    href="#" onClick={e => setFilter(e, {state: TaskFilterState.Cancelling})}>cancelling</a></span>);
            }
            if (props.totals.Queued > 0) {
                result.push(<span key="queued">{props.totals.Queued} task{addSIfNeeded(props.totals.Queued)} <a
                    href="#" onClick={e => setFilter(e, {state: TaskFilterState.Queued})}>waiting in queue</a></span>);
            }
        }

        if (result.length === 0) {
            return "No active tasks";
        }

        if (result.length === 1) {
            return result;
        }

        const delimited = result.map((v, i) => {
            if (i === result.length - 1) {
                return v;
            }
            if (i === result.length - 2) {
                return [v, <span> and </span>];
            }
            return [v, <span>, </span>];
        });
        return delimited;
    };

    return <div className={styles.stats}>
        {stat()}
    </div>;
};

function createEmptyFilter(): Filter {
    return {};
}

export default Tasks;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/areas/tasks/components/Tasks/Tasks.tsx